/**
 * Transactions (FR-TXN-01 to FR-TXN-21).
 *
 * Pure: shape, validation, derived fields, and — most importantly — how a transaction
 * moves account balances and which of the three dashboard figures it lands in.
 *
 * Those last two are where a quiet error is most expensive, so they live here with
 * tests rather than inside a Firestore write.
 */

import type { AccountType } from './accounts';
import { isLiability } from './accounts';
import { DEFAULT_FY_START_MONTH, fyKeyOf, isValidDateKey, monthKeyOf, type DateKey, type FyKey, type MonthKey } from './dates';
import { negate, type Paise, type Ppm } from './money';

export type TransactionType = 'income' | 'expense' | 'transfer';

export const TRANSACTION_TYPES: TransactionType[] = ['income', 'expense', 'transfer'];

export interface Attachment {
  path: string;
  name: string;
  sizeBytes: number;
  contentType: string;
}

export interface Transaction {
  id: string;
  type: TransactionType;

  dateKey: DateKey;
  monthKey: MonthKey;
  fyKey: FyKey;

  accountId: string;
  /** Transfers only. Must differ from `accountId` (FR-TXN-04). */
  toAccountId: string | null;

  amountPaise: Paise;
  /** The text the user typed, when it was an expression (FR-TXN-08). */
  amountExpression: string | null;
  currency: 'INR' | 'USD';
  /** Frozen at entry. Never recomputed (FR-CUR-03). */
  ratePpm: Ppm;
  rateIsStale: boolean;
  amountInrPaise: Paise;
  /** Cross-currency transfers, where the receiving side differs. */
  toAmountPaise: Paise | null;

  categoryId: string | null;
  subcategoryId: string | null;
  description: string | null;
  payee: string | null;
  referenceNo: string | null;
  notes: string | null;
  tagIds: string[];
  isBusiness: boolean;
  searchTokens: string[];
  attachments: Attachment[];

  receivableId: string | null;
  loanId: string | null;
  instalmentNo: number | null;
  principalPaise: Paise | null;
  interestPaise: Paise | null;

  migrated: boolean;
  importBatchId: string | null;
  needsEnrichment: boolean;
  quickCaptured: boolean;

  deleted: boolean;
  enteredBy: string;
}

/** Everything needed to create one. Optional fields default in `buildTransaction`. */
export interface TransactionInput {
  type: TransactionType;
  dateKey: DateKey;
  accountId: string;
  amountPaise: Paise;
  toAccountId?: string | null;
  amountExpression?: string | null;
  currency?: 'INR' | 'USD';
  ratePpm?: Ppm;
  rateIsStale?: boolean;
  toAmountPaise?: Paise | null;
  categoryId?: string | null;
  subcategoryId?: string | null;
  description?: string | null;
  payee?: string | null;
  referenceNo?: string | null;
  notes?: string | null;
  tagIds?: string[];
  isBusiness?: boolean;
  receivableId?: string | null;
  loanId?: string | null;
  instalmentNo?: number | null;
  principalPaise?: Paise | null;
  interestPaise?: Paise | null;
  migrated?: boolean;
  importBatchId?: string | null;
  needsEnrichment?: boolean;
  quickCaptured?: boolean;
}

export const INR_RATE_PPM = 1_000_000;

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

export interface ValidationIssue {
  field: string;
  message: string;
}

/**
 * FR-TXN-02: **only date, account and amount are mandatory.** Everything else is
 * optional and must never block a save — 28% of the imported rows carry nothing but the
 * word "Expenses", and the app has to accept that shape of entry as readily as a fully
 * described one.
 */
export function validateTransaction(input: TransactionInput): ValidationIssue[] {
  const issues: ValidationIssue[] = [];

  if (!isValidDateKey(input.dateKey)) {
    issues.push({ field: 'dateKey', message: 'A valid date is required.' });
  }
  if (!input.accountId) {
    issues.push({ field: 'accountId', message: 'An account is required.' });
  }
  if (!Number.isSafeInteger(input.amountPaise)) {
    issues.push({ field: 'amountPaise', message: 'An amount is required.' });
  }
  if (!TRANSACTION_TYPES.includes(input.type)) {
    issues.push({ field: 'type', message: 'Unknown transaction type.' });
  }

  if (input.type === 'transfer') {
    if (!input.toAccountId) {
      issues.push({ field: 'toAccountId', message: 'A transfer needs a destination account.' });
    } else if (input.toAccountId === input.accountId) {
      issues.push({ field: 'toAccountId', message: 'A transfer cannot go to the same account.' });
    }
  }

  return issues;
}

// ---------------------------------------------------------------------------
// Derived fields
// ---------------------------------------------------------------------------

/**
 * Search tokens (AD-07).
 *
 * Capped: Firestore indexes every array element, and an unbounded token list on 60,000
 * documents is a large index for little benefit. Single characters are dropped — they
 * match everything and narrow nothing.
 */
export function buildSearchTokens(
  parts: (string | null | undefined)[],
  max = 40,
): string[] {
  const tokens = new Set<string>();
  for (const part of parts) {
    if (!part) continue;
    for (const token of part.toLowerCase().split(/[^a-z0-9]+/)) {
      if (token.length > 1) tokens.add(token);
      if (tokens.size >= max) break;
    }
  }
  return [...tokens].slice(0, max);
}

export function buildTransaction(
  id: string,
  input: TransactionInput,
  enteredBy: string,
  fyStartMonth = DEFAULT_FY_START_MONTH,
): Transaction {
  const currency = input.currency ?? 'INR';
  const ratePpm = input.ratePpm ?? INR_RATE_PPM;

  return {
    id,
    type: input.type,
    dateKey: input.dateKey,
    monthKey: monthKeyOf(input.dateKey),
    fyKey: fyKeyOf(input.dateKey, fyStartMonth),
    accountId: input.accountId,
    toAccountId: input.type === 'transfer' ? (input.toAccountId ?? null) : null,
    amountPaise: input.amountPaise,
    amountExpression: input.amountExpression ?? null,
    currency,
    ratePpm,
    rateIsStale: input.rateIsStale ?? false,
    // Denormalised so every sum works in one currency (SD-05). Written once, from the
    // frozen rate, and never recomputed.
    amountInrPaise:
      currency === 'INR'
        ? input.amountPaise
        : Math.round((input.amountPaise * ratePpm) / INR_RATE_PPM),
    toAmountPaise: input.toAmountPaise ?? null,
    categoryId: input.type === 'transfer' ? null : (input.categoryId ?? null),
    subcategoryId: input.type === 'transfer' ? null : (input.subcategoryId ?? null),
    description: input.description ?? null,
    payee: input.payee ?? null,
    referenceNo: input.referenceNo ?? null,
    notes: input.notes ?? null,
    tagIds: input.tagIds ?? [],
    isBusiness: input.isBusiness ?? false,
    searchTokens: buildSearchTokens([
      input.description,
      input.payee,
      input.notes,
      input.referenceNo,
    ]),
    attachments: [],
    receivableId: input.receivableId ?? null,
    loanId: input.loanId ?? null,
    instalmentNo: input.instalmentNo ?? null,
    principalPaise: input.principalPaise ?? null,
    interestPaise: input.interestPaise ?? null,
    migrated: input.migrated ?? false,
    importBatchId: input.importBatchId ?? null,
    needsEnrichment: input.needsEnrichment ?? false,
    quickCaptured: input.quickCaptured ?? false,
    deleted: false,
    enteredBy,
  };
}

// ---------------------------------------------------------------------------
// Balance effects
// ---------------------------------------------------------------------------

export interface BalanceEffect {
  accountId: string;
  deltaPaise: Paise;
}

/**
 * How a transaction moves account balances.
 *
 * Uniform across every account type because liabilities are stored negative
 * (SCHEMA §11.2). A card purchase subtracts, taking the balance further negative and the
 * outstanding up; a bill payment is a transfer that adds to the card, bringing the
 * outstanding down. No per-type special case, which is what stops a sign error creeping
 * into one branch.
 *
 * A transfer produces **two effects from one record** (FR-TXN-04) — there is no second
 * document to fall out of step with the first.
 */
export interface BalanceOptions {
  /**
   * The cutover date. Transactions dated before it are **archive**: they are counted
   * against the account but move no balance (FR-ACC-06a/06b).
   *
   * Omit for rollups, which want the flows in every month including migrated ones.
   */
  cutover?: DateKey;
}

/** What `balanceEffects` needs. `dateKey` matters only when a cutover is supplied. */
export type BalanceInput = Pick<
  Transaction,
  'type' | 'accountId' | 'toAccountId' | 'amountPaise' | 'toAmountPaise'
> &
  Partial<Pick<Transaction, 'dateKey' | 'interestPaise'>>;

export function balanceEffects(
  txn: BalanceInput,
  options: BalanceOptions = {},
): BalanceEffect[] {
  const effects = rawEffects(txn);

  // The archive does not roll forward, and this is the single line that enforces it.
  //
  // `CLAUDE.md`, 3 Aug 2026: "everything before 1 Aug has already been reviewed and
  // reconciled manually, and the August sheet holds the position as of today. Jan–Jul
  // must not be tallied forward to produce the August balances." The seeded openings
  // *are* the 1 August position, so applying the seven migrated months on top of them
  // double-counts every one.
  //
  // It is not merely double-counting either: the workbook records **no income at all**
  // (income lives only inside `Add/Sub` expressions), so replaying the archive is seven
  // months of outflow with nothing coming in. IDFC lands at −3,17,804 against a real
  // 44,094.75. The archive is history to report on, not a ledger to replay.
  //
  // The accounts are still *touched*, with a zero delta, so `transactionCount` moves —
  // that count is what the rules read to decide whether an account may be deleted
  // (FR-ACC-03), and an account holding 134 migrated rows must not look empty.
  if (options.cutover && txn.dateKey !== undefined && txn.dateKey < options.cutover) {
    return effects.map((effect) => ({ ...effect, deltaPaise: 0 }));
  }

  return effects;
}

function rawEffects(txn: BalanceInput): BalanceEffect[] {
  switch (txn.type) {
    case 'income':
      return [{ accountId: txn.accountId, deltaPaise: txn.amountPaise }];
    case 'expense':
      return [{ accountId: txn.accountId, deltaPaise: negate(txn.amountPaise) }];
    case 'transfer': {
      if (!txn.toAccountId) return [];
      return [
        { accountId: txn.accountId, deltaPaise: negate(txn.amountPaise) },
        { accountId: txn.toAccountId, deltaPaise: landingPaise(txn) },
      ];
    }
  }
}

/**
 * How much of a transfer actually reaches the destination.
 *
 * Normally all of it. Two cases take a cut:
 *
 *  · **Cross-currency** — `toAmountPaise` states what landed, which may differ from what
 *    left after conversion.
 *  · **A loan EMI** — only the *principal* reduces the loan (FR-LOAN-06). The interest
 *    portion leaves the bank account but never reaches the lender's principal; it is
 *    consumed as an expense. So the two sides of this transfer deliberately do **not**
 *    balance, and that difference is the interest.
 *
 * Getting this wrong is not subtle in its effect but is silent in its cause: crediting
 * the full EMI against the loan would have the car paid off years early on paper. The
 * seeded 8,78,750 exists precisely because the lender's own schedule says principal falls
 * by 94,395 over the seven migrated months, not by the 1,59,446 that was paid.
 */
export function landingPaise(
  txn: Pick<Transaction, 'amountPaise' | 'toAmountPaise'> &
    Partial<Pick<Transaction, 'interestPaise'>>,
): Paise {
  if (txn.toAmountPaise !== null && txn.toAmountPaise !== undefined) return txn.toAmountPaise;
  return txn.amountPaise - (txn.interestPaise ?? 0);
}

export interface AccountDelta {
  balancePaise: Paise;
  transactionCount: number;
}

/**
 * Collapse applied and reversed effects into one change per account.
 *
 * An edit reverses the old effects and applies the new ones in the same batch. When both
 * touch the same account they must combine into a single update — Firestore rejects two
 * writes to one document in a batch, and separate updates would race.
 *
 * `transactionCount` moves with the account, which matters because the security rules
 * read it to decide whether an account may be deleted (FR-ACC-03). Moving a transaction
 * off an account has to make that account deletable again.
 */
export function collectDeltas(
  applied: BalanceEffect[],
  reversed: BalanceEffect[],
): Map<string, AccountDelta> {
  const deltas = new Map<string, AccountDelta>();
  const bump = (accountId: string, balance: Paise, count: number) => {
    const current = deltas.get(accountId) ?? { balancePaise: 0, transactionCount: 0 };
    deltas.set(accountId, {
      balancePaise: current.balancePaise + balance,
      transactionCount: current.transactionCount + count,
    });
  };
  for (const effect of reversed) bump(effect.accountId, effect.deltaPaise, -1);
  for (const effect of applied) bump(effect.accountId, effect.deltaPaise, 1);
  return deltas;
}

/** Reversing effects, for edits and soft deletes. */
export function reverseEffects(effects: BalanceEffect[]): BalanceEffect[] {
  return effects.map((e) => ({ accountId: e.accountId, deltaPaise: negate(e.deltaPaise) }));
}

// ---------------------------------------------------------------------------
// The three dashboard figures (FR-DASH-02)
// ---------------------------------------------------------------------------

export type OutflowKind = 'expense' | 'savings' | 'internal' | 'lending' | 'none';

/**
 * Which figure a transaction contributes to.
 *
 * `CLAUDE.md`, confirmed 3 Aug 2026:
 *   Total outflow = Expenses + Savings & investments.
 *
 * Everything else is excluded, and each exclusion has a reason:
 *   · `internal` — a card bill payment or bank-to-bank move. Counting it would
 *     double-count against the purchase it settles. This exclusion is exactly what
 *     produces the corrected column in FR-DASH-05.
 *   · `lending` — money lent out belongs to receivables. Counting it would make the
 *     figure *fall* when someone repays, which is not spending.
 */
export function classifyOutflow(
  txn: Pick<Transaction, 'type' | 'toAccountId' | 'receivableId'>,
  accountType: (accountId: string) => AccountType | undefined,
): OutflowKind {
  if (txn.type === 'income') return 'none';

  if (txn.type === 'expense') {
    return txn.receivableId ? 'lending' : 'expense';
  }

  // Transfers: where the money lands decides.
  if (!txn.toAccountId) return 'none';
  if (txn.receivableId) return 'lending';

  const destination = accountType(txn.toAccountId);
  if (destination === 'savings_pot' || destination === 'investment') return 'savings';
  return 'internal';
}

/** FR-TXN-05: transfers never appear in income or expense totals. */
export function countsTowardIncome(txn: Pick<Transaction, 'type'>): boolean {
  return txn.type === 'income';
}

export function countsTowardExpenses(
  txn: Pick<Transaction, 'type' | 'toAccountId' | 'receivableId'>,
  accountType: (accountId: string) => AccountType | undefined,
): boolean {
  return classifyOutflow(txn, accountType) === 'expense';
}

/**
 * Is this a payment against a credit card or loan?
 *
 * Used to label a transfer clearly in the list — "Card payment" reads better than a bare
 * transfer, and it is the category most often miscategorised as an expense.
 */
export function isLiabilityPayment(
  txn: Pick<Transaction, 'type' | 'toAccountId'>,
  accountType: (accountId: string) => AccountType | undefined,
): boolean {
  if (txn.type !== 'transfer' || !txn.toAccountId) return false;
  const destination = accountType(txn.toAccountId);
  return destination !== undefined && isLiability(destination);
}
